import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
    Box,
    Grid,
    Chip,
    CircularProgress,
    Tooltip
} from '@mui/material';
import {
    Person as PersonIcon,
    Email as EmailIcon,
    Storage as StorageIcon,
    Public as PublicIcon,
    Lock as LockIcon,
    Security as SecurityIcon,
    InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import { fetchAuthSession } from '@aws-amplify/auth';
import { WHISPERBI_SECURITY_API } from '../../env';

const USER_ACCOUNT_BASE_URL = WHISPERBI_SECURITY_API;

/**
 * Fetch owner information from Cognito token or backend
 */
async function fetchOwnerInfo(accountId) {
    try {
        const tokens = (await fetchAuthSession()).tokens;
        const idToken = tokens?.idToken;
        
        if (!idToken || !accountId) {
            return { name: 'No disponible', email: 'No disponible' };
        }

        // Extract email and name from the token payload if this is the current user
        const tokenPayload = idToken.payload;
        const currentUserId = tokenPayload.sub;
        
        // If the account_id matches the current user, use their info from the token
        if (currentUserId === accountId) {
            const email = tokenPayload.email || 'No disponible';
            const name = tokenPayload.name || 
                        tokenPayload['cognito:username'] || 
                        tokenPayload.given_name || 
                        email.split('@')[0] || 
                        'Usuario';
            return { name, email };
        }

        // For other users, try to fetch from backend
        // Note: This requires the backend to return user info
        const response = await fetch(`${USER_ACCOUNT_BASE_URL}/${accountId}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${idToken.toString()}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            console.warn('Could not fetch owner info:', response.status);
            return { name: 'Usuario', email: 'No disponible' };
        }

        const data = await response.json();
        
        // Extract company contact email as fallback
        const companyEmail = data.company?.contact_email || null;
        const companyName = data.company?.name || null;
        
        // Since user email is not in USER_ACCOUNT table, use company info
        return { 
            name: companyName || 'Usuario',
            email: companyEmail || 'No disponible'
        };
    } catch (error) {
        console.error('Error fetching owner info:', error);
        return { name: 'Usuario', email: 'No disponible' };
    }
}

/**
 * Component for viewing data source details in read-only mode
 */
const DataSourceViewDialog = ({
    open,
    onClose,
    source
}) => {
    const [ownerInfo, setOwnerInfo] = useState({ name: 'Cargando...', email: 'Cargando...' });
    const [isLoadingOwner, setIsLoadingOwner] = useState(false);

    useEffect(() => {
        if (open && source && source.account_id) {
            setIsLoadingOwner(true);
            fetchOwnerInfo(source.account_id)
                .then(info => {
                    setOwnerInfo(info);
                    setIsLoadingOwner(false);
                })
                .catch(err => {
                    console.error('Error loading owner info:', err);
                    setOwnerInfo({ name: 'No disponible', email: 'No disponible' });
                    setIsLoadingOwner(false);
                });
        }
    }, [open, source]);

    if (!source) return null;

    const InfoRow = ({ label, value, icon: Icon }) => (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                {Icon && <Icon sx={{ fontSize: 18, mr: 1, color: 'var(--wb-primary)' }} />}
                <Typography variant="caption" sx={{ color: 'var(--wb-text-soft)', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                    {label}
                </Typography>
            </Box>
            <Typography variant="body1" sx={{ color: 'var(--wb-text-strong)', pl: Icon ? 3.5 : 0 }}>
                {value || 'N/A'}
            </Typography>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: 'var(--wb-bg-panel)',
                    color: 'var(--wb-text-strong)'
                }
            }}
        >
            <DialogTitle sx={{ borderBottom: '1px solid var(--wb-overlay-10)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Detalles de la Conexión</Typography>
                    <Chip
                        label={source.is_public ? 'Pública' : 'Privada'}
                        icon={source.is_public ? <PublicIcon /> : <LockIcon />}
                        size="small"
                        sx={{
                            bgcolor: source.is_public ? 'var(--wb-x-bg-0095ff33, rgba(0, 149, 255, 0.2))' : 'var(--wb-x-bg-ff980033, rgba(255, 152, 0, 0.2))',
                            color: source.is_public ? 'var(--wb-primary)' : 'var(--wb-x-text-ff9800, #ff9800)',
                            '& .MuiChip-icon': {
                                color: source.is_public ? 'var(--wb-primary)' : 'var(--wb-x-text-ff9800, #ff9800)'
                            }
                        }}
                    />
                </Box>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 3, color: 'var(--wb-text-soft)' }}>
                    Esta conexión pertenece a otro usuario. Solo puedes ver los detalles generales.
                </Typography>

                <Grid container spacing={3}>
                    {/* Owner Information */}
                    <Grid item xs={12}>
                        <Box sx={{ 
                            bgcolor: 'var(--wb-bg-elevated)', 
                            p: 2, 
                            borderRadius: 2,
                            border: '1px solid var(--wb-overlay-10)'
                        }}>
                            <Typography variant="subtitle2" sx={{ color: 'var(--wb-primary)', mb: 2, fontWeight: 600 }}>
                                Información del Propietario
                            </Typography>
                            {isLoadingOwner ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                                    <CircularProgress size={20} sx={{ color: 'var(--wb-primary)' }} />
                                    <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)' }}>
                                        Cargando información del propietario...
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <InfoRow 
                                        label="Nombre del Propietario" 
                                        value={ownerInfo.name}
                                        icon={PersonIcon}
                                    />
                                    <InfoRow 
                                        label="Correo del Propietario" 
                                        value={ownerInfo.email}
                                        icon={EmailIcon}
                                    />
                                </>
                            )}
                        </Box>
                    </Grid>

                    {/* Connection Information */}
                    <Grid item xs={12}>
                        <Box sx={{ 
                            bgcolor: 'var(--wb-bg-elevated)', 
                            p: 2, 
                            borderRadius: 2,
                            border: '1px solid var(--wb-overlay-10)'
                        }}>
                            <Typography variant="subtitle2" sx={{ color: 'var(--wb-primary)', mb: 2, fontWeight: 600 }}>
                                Información de la Conexión
                            </Typography>
                            <InfoRow 
                                label="Nombre" 
                                value={source.name}
                                icon={StorageIcon}
                            />
                            <InfoRow 
                                label="Tipo" 
                                value={source.type}
                            />
                            <InfoRow 
                                label="Descripción" 
                                value={source.description}
                            />
                            {source.type === 'SQL' && (
                                <>
                                    <InfoRow 
                                        label="Base de Datos" 
                                        value={source.databaseName}
                                    />
                                    <InfoRow 
                                        label="Versión" 
                                        value={source.databaseVersion}
                                    />
                                </>
                            )}
                            <InfoRow
                                label="Estado"
                                value={
                                    source.status === 'connected' ? 'Conectado' :
                                    source.status === 'error'
                                        ? (source.errorMessageUser || 'No se pudo procesar la conexión.')
                                        : 'Pendiente'
                                }
                            />
                            {source.status === 'error' && source.errorMessage && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: -1.5, mb: 2, pl: 0 }}>
                                    <Tooltip title={source.errorMessage} arrow placement="top">
                                        <InfoOutlinedIcon
                                            sx={{ fontSize: 18, mr: 1, color: 'var(--wb-danger-strong)', cursor: 'help', flexShrink: 0 }}
                                        />
                                    </Tooltip>
                                    <Typography variant="caption" sx={{ color: 'var(--wb-text-dim)' }}>
                                        Pasa el cursor sobre el ícono para ver el detalle técnico del error.
                                    </Typography>
                                </Box>
                            )}
                            <InfoRow
                                label="Última Sincronización"
                                value={source.lastSync}
                            />
                            {source.type === 'SQL' && (
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                                    <SecurityIcon sx={{ fontSize: 18, mr: 1, color: 'var(--wb-primary)' }} />
                                    <Typography variant="caption" sx={{ color: 'var(--wb-text-soft)' }}>
                                        SSL: {source.use_ssl ? 'Habilitado' : 'Deshabilitado'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ borderTop: '1px solid var(--wb-overlay-10)', p: 2 }}>
                <Button 
                    onClick={onClose}
                    variant="contained"
                    sx={{
                        bgcolor: 'var(--wb-primary)',
                        '&:hover': { bgcolor: 'var(--wb-primary-hover)' }
                    }}
                >
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DataSourceViewDialog;
